import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useChat } from '../../hooks/useChat';
import NavigationBar from '../../components/ui/NavigationBar';
import ChatMessage from './components/ChatMessage';
import MessageInput from './components/MessageInput';
import TypingIndicator from './components/TypingIndicator';
import WelcomeScreen from './components/WelcomeScreen';
import ChatHeader from './components/ChatHeader';

const AIChatInterface = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [showWelcome, setShowWelcome] = useState(true);
  const chatContainerRef = useRef(null);

  const { response, isLoading, error, sendMessage } = useChat(
    'OPEN_AI',
    'gpt-5',
    true
  );

  const mockStudentProfile = {
    name: 'Amadou Diallo',
    initials: 'AD',
    interests: ['Sciences', 'Technologie', 'Innovation'],
    academicLevel: 'Terminale S',
    strengths: ['Mathématiques', 'Physique', 'Informatique']
  };

  const quickSuggestions = [
    'Quelles filières correspondent à mes compétences en sciences ?',
    'Quels sont les débouchés en ingénierie au Sénégal ?',
    'Comment choisir entre médecine et pharmacie ?',
    'Quelles écoles offrent des bourses d\'études ?'
  ];

  useEffect(() => {
    if (error) {
      const errorMessage = error?.message || 'Une erreur est survenue';
      
      // Gestion spécifique de l'erreur 429 (rate limit)
      if (errorMessage?.includes('429') || errorMessage?.toLowerCase()?.includes('rate limit')) {
        toast?.error(
          '⚠️ Limite de requêtes atteinte. Veuillez patienter quelques instants avant de réessayer.',
          { duration: 5000 }
        );
      } else if (errorMessage?.includes('401') || errorMessage?.includes('403')) {
        toast?.error(
          '🔒 Erreur d\'authentification. Veuillez contacter l\'administrateur.',
          { duration: 5000 }
        );
      } else if (errorMessage?.includes('500') || errorMessage?.includes('502') || errorMessage?.includes('503')) {
        toast?.error(
          '⚙️ Le service est temporairement indisponible. Veuillez réessayer dans quelques instants.',
          { duration: 5000 }
        );
      } else {
        toast?.error(`❌ ${errorMessage}`, { duration: 4000 });
      }
    }
  }, [error]);

  useEffect(() => {
    if (chatContainerRef?.current) {
      chatContainerRef.current.scrollTop = chatContainerRef?.current?.scrollHeight;
    }
  }, [messages, response]);

  useEffect(() => {
    if (response && !isLoading) {
      setMessages(prev => {
        const filtered = prev?.filter(msg => !msg?.isStreaming);
        return [
          ...filtered,
          {
            id: Date.now(),
            sender: 'ai',
            content: response,
            timestamp: new Date(),
            isStreaming: false
          }
        ];
      });
    }
  }, [response, isLoading]);

  const handleSendMessage = (messageText) => {
    if (showWelcome) {
      setShowWelcome(false);
    }

    const userMessage = {
      id: Date.now(),
      sender: 'user',
      userInitials: mockStudentProfile?.initials,
      content: messageText,
      timestamp: new Date(),
      status: 'sent'
    };

    setMessages(prev => [...prev, userMessage]);

    const systemPrompt = `Tu es un assistant d'orientation scolaire personnalisé pour les étudiants sénégalais. Voici le profil de l'étudiant:
- Nom: ${mockStudentProfile?.name}
- Niveau: ${mockStudentProfile?.academicLevel}
- Points forts: ${mockStudentProfile?.strengths?.join(', ')}
- Centres d'intérêt: ${mockStudentProfile?.interests?.join(', ')}

Ton rôle est de:
1. Fournir des recommandations de filières adaptées au profil de l'étudiant
2. Donner des informations sur les débouchés professionnels au Sénégal et en Afrique de l'Ouest
3. Suggérer des écoles et universités pertinentes
4. Répondre aux questions sur l'orientation avec des informations contextualisées pour le système éducatif sénégalais

Réponds de manière claire, structurée et encourageante. Utilise des emojis pour rendre tes réponses plus engageantes.`;

    const conversationHistory = messages?.filter(msg => msg?.sender === 'user' || (msg?.sender === 'ai' && !msg?.isStreaming))?.map(msg => ({
        role: msg?.sender === 'user' ? 'user' : 'assistant',
        content: msg?.content
      }));

    const apiMessages = [
      { role: 'system', content: systemPrompt },
      ...conversationHistory,
      { role: 'user', content: messageText }
    ];

    sendMessage(apiMessages, { 
      max_completion_tokens: 256
    });

    setTimeout(() => {
      setMessages(prev => 
        prev?.map(msg => 
          msg?.id === userMessage?.id ? { ...msg, status: 'read' } : msg
        )
      );
    }, 500);
  };

  const handleQuickStart = (question) => {
    handleSendMessage(question);
  };

  const handleNewChat = () => {
    setMessages([]);
    setShowWelcome(true);
  };

  const handleActionClick = (actionType, data) => {
    if (actionType === 'navigate' && data) {
      navigate(data);
    } else if (actionType === 'view-field') {
      navigate('/study-field-results');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" />
      <NavigationBar />
      <div className="content-area">
        <div className="max-w-6xl mx-auto h-[calc(100vh-80px)] md:h-[calc(100vh-96px)] flex flex-col">
          {showWelcome ? (
            <WelcomeScreen onQuickStart={handleQuickStart} />
          ) : (
            <>
              <ChatHeader 
                onNewChat={handleNewChat}
                messageCount={messages?.length}
              />

              <div 
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto px-4 py-6 md:px-6 md:py-8 lg:px-8"
              >
                <div className="max-w-4xl mx-auto">
                  {messages?.filter(msg => !msg?.isStreaming)?.map((message) => (
                    <ChatMessage
                      key={message?.id}
                      message={message}
                      onSuggestionClick={handleSendMessage}
                      onActionClick={handleActionClick}
                    />
                  ))}
                  
                  {isLoading && (
                    <>
                      {response ? (
                        <ChatMessage
                          message={{
                            id: 'streaming',
                            sender: 'ai',
                            content: response,
                            timestamp: new Date(),
                            isStreaming: true
                          }}
                          onSuggestionClick={handleSendMessage}
                          onActionClick={handleActionClick}
                        />
                      ) : (
                        <TypingIndicator />
                      )}
                    </>
                  )}
                </div>
              </div>

              <MessageInput
                onSendMessage={handleSendMessage}
                disabled={isLoading}
                suggestions={messages?.length === 0 ? quickSuggestions?.slice(0, 2) : null}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIChatInterface;